import {useEffect, useMemo, useState} from "react";
import {IntlProvider, FormattedMessage} from "react-intl";
import "./App.css";
import moment from "moment";
import queryString from "query-string";
import dayjs from "dayjs";

import english from "./locales/en-US";
import vietnam from "./locales/vi-VN";
import chinese_simplified from "./locales/zh-CN";
import chinese_traditional from "./locales/zh-HK";
import malay from "./locales/ms-MY";
// import thai from "./locales/th-TH";

import countriesList from "./data/countries.json";
import {getApiUrlBrandName} from "./services/public_functions";
import Topbar from "./components/Topbar";
import FiltersBox from "./components/FiltersBox";
import EcoCalendarTable from "./components/EcoCalendarTable";
import ChartsModal from "./components/ChartsModal";
import RegisterModal from "./components/RegisterModal";
import {BrandSettingsAPI, EconomicCalendarAPI, RegistrationModuleAPI} from "./services/api";

const initialState_countriesSelected = {
    AU: false,
    AT: false,
    BE: false,
    BR: false,
    CA: false,
    CN: false,
    CY: false,
    CZ: false,
    DK: false,
    EA: false,
    FI: false,
    FR: false,
    DE: false,
    GR: false,
    HK: false,
    HU: false,
    IN: false,
    ID: false,
    IE: false,
    IT: false,
    JP: false,
    LU: false,
    MX: false,
    NL: false,
    NZ: false,
    NO: false,
    PL: false,
    PT: false,
    RO: false,
    RU: false,
    SG: false,
    ZA: false,
    KR: false,
    ES: false,
    SE: false,
    CH: false,
    TR: false,
    GB: false,
    US: false,
};

const initialState_zoneSelected = {
    g7: false,
    european_union: false,
    all: false,
};

const locales = {
    en: english,
    vn: vietnam,
    cn: chinese_simplified,
    zh: chinese_traditional,
    ms: malay,
    // th: thai,
};

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const [currentLocale, setCurrentLocale] = useState("en");
    const [brandSettings, setBrandSettings] = useState({});
    const [tableData, setTableData] = useState({});
    const [isRegisterModalVisible, setIsRegisterModalVisible] = useState(false);
    const [isChartsModalVisible, setIsChartsModalVisible] = useState(false);
    const [isResetBtnShow, setIsResetBtnShow] = useState(false);
    const [testTime, setTestTime] = useState("");
    const [timezone, setTimezone] = useState("0");
    const [date, setDate] = useState([]);
    const [daySelected, setDaySelected] = useState("");
    const [importance, setImportance] = useState([]);
    const [eventNum, setEventNum] = useState(0);
    const [zoneSelected, setZoneSelected] = useState(initialState_zoneSelected);
    const [countriesSelected, setCountriesSelected] = useState(initialState_countriesSelected);
    const [modalPosition, setModalPosition] = useState("1");
    const [selectedEvent, setSelectedEvent] = useState("");
    const [chartModalActiveTab, setChartModalActiveTab] = useState("1");
    const [nextEventTime, setNextEventTime] = useState("");

    const params = queryString.parse(window.location.search);
    let domain = params["domain"] || "blueocex.com";
    let theme = params["theme"]; // light or dark
    if (!theme) {
        theme = "light";
    }

    let lang = params["lang"];
    let isCRM = domain?.includes("member");

    let langForAPI;
    switch (lang) {
        case "zh-CN":
        case "zh-HK":
        case "cn":
        case "zh":
            langForAPI = "zh-hans";
            break;
        default:
            langForAPI = "en";
            break;
    }

    // const apiBaseUrl = "https://secure.";
    const crmBaseUrl = "https://member.";
    // let api_url = `${apiBaseUrl}${domain}`;
    let crm_url = `${crmBaseUrl}${domain}`;
    let brandName = getApiUrlBrandName(domain).brandName;
    let oldAdminPanel = getApiUrlBrandName(domain).oldAdminPanel;

    switch (true) {
        case domain.includes("www."):
            // api_url = `${apiBaseUrl}${domain.replace("www", "secure")}`;
            crm_url = `${crmBaseUrl}${domain.replace("www", "")}`;
            break;
        case domain.includes("member."):
            // api_url = `${apiBaseUrl}${domain.replace("member", "secure")}`;
            break;
        default:
            // api_url = `${apiBaseUrl}${domain}`;
            crm_url = `${crmBaseUrl}${domain}`;
            break;
    }

    const getBrandSettings = async () => {
        try {
            let res = await BrandSettingsAPI();
            if (res) {
                let result = res?.data?.getUserBrandSetting;
                if (oldAdminPanel) {
                    result = res;
                }
                setBrandSettings((prevState) => ({...prevState, ...result}));
            }
        } catch (err) {
            console.log("err getBrandSettings", err);
        } finally {
        }
    };

    const getBrandRegistrationModuleSettings = async () => {
        try {
            let res = await RegistrationModuleAPI();
            if (res) {
                let result = res?.data?.getRegistrationModule;
                setBrandSettings((prevState) => ({
                    ...prevState,
                    demo_account_registration: result?.demoRegistration,
                    live_account_registration: result?.liveRegistration,
                }));
            }
        } catch (err) {
            console.log("err getBrandSettings", err);
        } finally {
        }
    };

    const getEconomicCalendar = async () => {
        setIsLoading(true);
        try {
            let filters = {};
            let imp = importance.length === 0 || importance.length === 3 ? "" : importance.join(",");

            const countryKeys = Object.keys(countriesSelected);
            let region = countryKeys.filter((region) => countriesSelected[region] === true).join(",");

            if (timezone) {
                filters.timezone = timezone;
            }
            if (date.length) {
                let startDate = dayjs(date[0]).format("YYYY-MM-DD");
                let endDate = dayjs(date[1]).format("YYYY-MM-DD");
                let range = `${startDate} - ${endDate}`;
                filters.date = range;
            }
            if (imp) {
                filters.importance = imp;
            }
            if (region) {
                filters.region = region;
            }
            if (langForAPI) {
                filters.lang = langForAPI;
            }

            let res = await EconomicCalendarAPI(filters);
            if (res.status === 1) {
                let num = 0;
                Object.keys(res.list).map((date) => (num += res.list[date].length));
                Object.keys(res.list).length > 0 &&
                    Object.keys(res.list).map((objDate) =>
                        res.list[objDate].map((event, i) => {
                            let eventTime = new Date(event.eventTime);
                            let currentTime = new Date();
                            let timezoneInMin = Number(-timezone) * 60;
                            let previousEvent = i !== 0 ? res.list[objDate][i - 1] : "";
                            let isPastEvent = eventTime < currentTime;
                            let isTodayEvent =
                                moment(eventTime).utcOffset(timezoneInMin).format("YYYY-MM-DD") ===
                                moment().utcOffset(timezoneInMin).format("YYYY-MM-DD");
                            if (!isPastEvent && isTodayEvent) {
                                if (previousEvent && new Date(previousEvent.eventTime) < currentTime) {
                                    setNextEventTime(event.eventTime);
                                } else if (!previousEvent) {
                                    setNextEventTime(event.eventTime);
                                }
                            }
                        })
                    );
                setEventNum(num);
                setTableData(res.list);
            }
        } catch (err) {
            console.log("err getEconomicCalendar", err);
        } finally {
            setIsLoading(false);
        }
    };

    const filterRegion = (region) => {
        let selectedCountries = {...countriesSelected};

        if (region === "g7" || region === "european_union" || region === "all") {
            let currentStatus = zoneSelected[region];
            countriesList[region].map((item) => (selectedCountries[item.value] = !currentStatus));
            setZoneSelected({
                ...zoneSelected,
                [region]: !currentStatus,
            });
        } else {
            selectedCountries[region] = !countriesSelected[region];
        }
        setCountriesSelected(selectedCountries);
        setIsResetBtnShow(true);
    };

    const filterImportance = (imp) => {
        let list = [...importance];
        if (list.includes(imp)) {
            list.splice(list.indexOf(imp), 1);
        } else {
            list.push(imp);
        }
        setImportance(list);
        setIsResetBtnShow(true);
    };

    const filterDate = (range, day) => {
        setDate(range);
        if (day) {
            setDaySelected(day);
        }
        setIsResetBtnShow(true);
    };

    const filterTimezone = (value) => {
        setTimezone(value);
    };

    const resetFilters = () => {
        setDate([]);
        setDaySelected("");
        setImportance([]);
        setCountriesSelected(initialState_countriesSelected);
        setZoneSelected(initialState_zoneSelected);
        setIsResetBtnShow(false);
    };

    // const getTimeLeft = (eventTime) => {
    //   var test = "";
    //   // setInterval(() => {
    //   let now = new Date().getTime();
    //   let distance = eventTime - now;
    //   if (distance > 0) {
    //     // Time calculations for days, hours, minutes and seconds
    //     let days = Math.floor(distance / (1000 * 60 * 60 * 24));
    //     let hours = Math.floor(
    //       (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    //     );
    //     let minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    //     let seconds = Math.floor((distance % (1000 * 60)) / 1000);

    //     let difference = "";
    //     if (days > 0) {
    //       difference += days === 1 ? `${days} day ` : `${days} days `;
    //     } else {
    //       difference += hours > 0 ? `${hours}h ` : "";

    //       difference += minutes > 0 ? `${minutes}min ` : "";

    //       difference +=
    //         hours < 1 && minutes < 1 && seconds > 0 ? `${seconds}s` : "";

    //       difference += seconds > 0 ? `${seconds}s` : "";
    //     }

    //     // Display the result in the element
    //     test = difference;
    //   }
    //   if (distance < 0) {
    //     // clearInterval(x);
    //     test = <i class="fas fa-check text-success"></i>;
    //   }
    //   // return test;
    //   // }, 1000);
    //   return test;
    // };

    const toggleRegisterModal = (e) => {
        // let modalPositionY = e.pageY > 426 ? e.pageY - 426 : e.pageY;
        setModalPosition(e.view.pageYOffset);
        // window.scrollTo(0, e.pageY);
        setIsRegisterModalVisible(!isRegisterModalVisible);
    };

    const toggleChartsModal = (event, key) => {
        if (event) {
            setSelectedEvent(event);
        }
        if (key) {
            setChartModalActiveTab(key);
        }
        setIsChartsModalVisible(!isChartsModalVisible);
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setTestTime(moment().format("MMMM Do YYYY, h:mm:ss a"));
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        getEconomicCalendar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timezone, date, importance, countriesSelected, testTime]);

    useEffect(() => {
        getBrandSettings();
        if (!oldAdminPanel) {
            getBrandRegistrationModuleSettings();
        }
        let currentTimezone = new Date().getTimezoneOffset() / 60;
        filterTimezone(currentTimezone.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const params = queryString.parse(window.location.search);
        let lang = params["lang"];
        if (lang && Object.keys(locales).includes(lang)) {
            setCurrentLocale(lang);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <IntlProvider locale={currentLocale} messages={locales[currentLocale].messages}>
            <div id={brandName} className={theme === "dark" ? "dark-theme" : ""}>
                <div id="economic-calendar" className={"row justify-content-center p-4"}>
                    <Topbar date={date} daySelected={daySelected} timezone={timezone} filterDate={filterDate} filterTimezone={filterTimezone} />
                    <div className="col-12 text-end mb-1">
                        {eventNum} {eventNum > 1 ? <FormattedMessage id="commons.events" /> : <FormattedMessage id="commons.event" />}
                    </div>
                    <FiltersBox
                        isResetBtnShow={isResetBtnShow}
                        resetFilters={resetFilters}
                        importance={importance}
                        filterImportance={filterImportance}
                        filterRegion={filterRegion}
                        countriesSelected={countriesSelected}
                    />
                    <EcoCalendarTable
                        tableData={tableData}
                        timezone={timezone}
                        nextEventTime={nextEventTime}
                        isCRM={isCRM}
                        toggleRegisterModal={toggleRegisterModal}
                        toggleChartsModal={toggleChartsModal}
                    />
                    <ChartsModal
                        theme={theme}
                        domain={domain}
                        isChartsModalVisible={isChartsModalVisible}
                        timezone={timezone}
                        selectedEvent={selectedEvent}
                        toggleChartsModal={toggleChartsModal}
                        chartModalActiveTab={chartModalActiveTab}
                        setChartModalActiveTab={setChartModalActiveTab}
                    />
                    <RegisterModal
                        brandName={brandName}
                        oldAdminPanel={oldAdminPanel}
                        brandSettings={brandSettings}
                        crm_url={crm_url}
                        isRegisterModalVisible={isRegisterModalVisible}
                        toggleRegisterModal={toggleRegisterModal}
                    />
                </div>
            </div>
        </IntlProvider>
    );
}

export default App;
